import auth from './db/auth.js'
import { reportServerReachable, reportServerUnreachable } from './serverReachability.js'

const api = {}
const API_URL = import.meta.env.VITE_API_URL

function defaultOptions(jwt = null) {
  const headers = {
    'Authorization': 'Bearer ' + (jwt || auth.getJwt()),
  }
  return {
    headers: new Headers(headers)
  }
}

function defaultPostOptions(body, jwt = null) {
  return {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + (jwt || auth.getJwt()),
    },
    body: body ? JSON.stringify(body) : undefined
  }
}

function checkAuthError(response) {
  if (response.status === 401) {
    auth.clear()
    window.location.href = "log-in";
  }
}

// Similar to internet connection check, but specifically for our server
api.canReachServer = async () => {
  try {
    await fetch(`${API_URL}/api/healthcheck`, {
      signal: AbortSignal.timeout(5000),
      cache: "no-store",
    })
    if (import.meta.env.DEV) {
      reportServerReachable()
    }
    return true
  } catch {
    if (import.meta.env.DEV) {
      reportServerUnreachable()
    }
    return false
  }
}

api.logIn = async (body) => {
  return fetch(`${API_URL}/api/login`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  })
    .then(async response => {
      const content = await response.json()
      if (!response.ok) {
        throw new Error(content.data.error)
      }
      // Ensure token is persisted to IDB before callers trigger syncDb().
      await auth.set(content.data)
      return content.data
    })
}

api.updateUserLocation = async (locationId) => {
  const response = await fetch(`${API_URL}/api/users/location`, {
    method: 'PATCH',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + auth.getJwt(),
    },
    body: JSON.stringify({ location_id: locationId }),
  })
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  await auth.set({ token: auth.getJwt(), user: content.data })
  return content.data
}

api.updateLocationUsePrintDialog = async (usePrintDialog, locationId) => {
  const body = { use_print_dialog: usePrintDialog }
  if (locationId != null) {
    body.location_id = locationId
  }
  const response = await fetch(`${API_URL}/api/locations-ext/use-print-dialog`, {
    method: 'PATCH',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + auth.getJwt(),
    },
    body: JSON.stringify(body),
  })
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

/** @type {(page?: number, perPage?: number, sortBy?: string, sortDirection?: string, search?: string, filters?: string[], options?: { jwt?: string, include?: string }) => Promise<any>} */
api.getOrders = async (page = 1, perPage = 25, sortBy = "ordered_at", sortDirection = "desc", search = "", filters = [], options = {}) => {
  const filtersParam = filters.length > 0 ? `&${filters.map(filter => `filters=${encodeURIComponent(filter)}`).join('&')}` : ''
  const includeParam = options.include ? `&include=${encodeURIComponent(options.include)}` : ''
  const response = await fetch(`${API_URL}/api/orders?page=${page}&per_page=${perPage}&sort_by=${sortBy}&sort_direction=${sortDirection}&search=${search}${filtersParam}${includeParam}`, defaultOptions(options.jwt))
  const responseBackup = response.clone()

  let content
  try {
    content = await response.json()
  } catch (e) {
    console.log(await responseBackup.text())
    throw e
  }

  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content
}

/** @type {(customerId: number|string, options?: { jwt?: string }) => Promise<any>} */
api.getCustomer = async (customerId, options = {}) => {
  const id = String(customerId ?? "").trim()
  const response = await fetch(`${API_URL}/api/customers/${id}`, defaultOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content?.data?.error ?? "Failed to fetch customer")
  }
  return content.data
}

/** GET /api/v2/customers — multi-term search (space = AND), addresses included via JOIN semantics */
api.getCustomersV2 = async (page = 1, perPage = 25, sortBy = "created_at", sortDirection = "desc", search = "", options = {}) => {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
    sort_by: sortBy,
    sort_direction: sortDirection,
    search,
  })
  const response = await fetch(`${API_URL}/api/v2/customers?${params}`, defaultOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content
}

api.getOrder = async (orderId, options = {}) => {
  const response = await fetch(`${API_URL}/api/orders/${orderId}`, defaultOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

api.getOrderFromTemplate = async (orderId, options = {}) => {
  const response = await fetch(`${API_URL}/api/orders/${orderId}/from-template`, defaultOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

api.getOrderByMaster = async (master, options = {}) => {
  const response = await fetch(`${API_URL}/api/orders?filters={master = ${master}}`, defaultOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  if (content.data?.length > 0) {
    return content.data[0]
  }
  return null
}

api.postOrder = async (body, options = {}) => {
  return fetch(`${API_URL}/api/orders`, defaultPostOptions(body, options.jwt))
    .then(async response => {
      const content = await response.json()
      if (!response.ok) {
        checkAuthError(response)
        throw new Error(content.data.error)
      }
      return content
    })
}

api.postOrderAsReceived = async (body, options = {}) => {
  return fetch(`${API_URL}/api/orders/received`, defaultPostOptions(body, options.jwt))
    .then(async response => {
      const content = await response.json()
      if (!response.ok) {
        checkAuthError(response)
        throw new Error(content.data.error)
      }
      return content.data
    })
}

api.updateOrder = async (orderId, body, options = {}) => {

  const headers = new Headers()
  headers.append('Accept', 'application/json')
  headers.append('Content-Type', 'application/json')
  headers.append('Authorization', 'Bearer ' + (options.jwt || auth.getJwt()))
  if (options.offlineHandle) {
    orderId = "$handle$" // special code
    headers.append("X-Upstream-Handle", options.offlineHandle)
  }

  return fetch(`${API_URL}/api/orders/${orderId}`, {
    method: 'PUT',
    headers: headers,
    body: JSON.stringify(body)
  })
    .then(async response => {
      const content = await response.json()
      if (!response.ok) {
        checkAuthError(response)
        throw new Error(content.data.error)
      }
      return content
    })
}

/** @type {(body: any, options?: { jwt?: string }) => Promise<any>} */
api.postOrderV2 = async (body, options = {}) => {
  const response = await fetch(`${API_URL}/api/orders/v2`, defaultPostOptions(body, options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

api.confirmOrder = async (orderId, staffInitial, options = {}) => {
  const body = {
    "staff_initial": staffInitial
  }
  return fetch(`${API_URL}/api/orders/${orderId}/confirm`, defaultPostOptions(body, options.jwt))
    .then(async response => {
      const content = await response.json()
      if (!response.ok) {
        checkAuthError(response)
        throw new Error(content.data.error)
      }
      return content
    })
}

api.unconfirmOrder = async (orderId, options = {}) => {
  return fetch(`${API_URL}/api/orders/${orderId}/unconfirm`, defaultPostOptions({}, options.jwt))
    .then(async response => {
      const content = await response.json()
      if (!response.ok) {
        checkAuthError(response)
        throw new Error(content.data.error)
      }
      return content
    })
}

api.getManifests = async (page = 1, perPage = 25, sortBy = "created_date", sortDirection = "desc", search = "", location = "", year = "", options = {}) => {
  const response = await fetch(`${API_URL}/api/manifests?page=${page}&per_page=${perPage}&sort_by=${sortBy}&sort_direction=${sortDirection}&search=${search}&location=${location}&year=${year}&updated_after=${options.updatedAfter ?? ""}`, defaultOptions(options.jwt))
  const responseBackup = response.clone()

  let content
  try {
    content = await response.json()
  } catch (e) {
    console.log(await responseBackup.text())
    throw e
  }

  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content
}

api.getManifest = async (manifestId, options = {}) => {
  const response = await fetch(`${API_URL}/api/manifests/${manifestId}`, defaultOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content
}

api.shipManifest = async (manifestId, lodgeCode, manifestNumber, file, options = {}) => {
  const data = new FormData()
  data.append('file', file, `manifest-${lodgeCode}-${manifestNumber}.pdf`)

  const headers = new Headers()
  headers.append('Authorization', 'Bearer ' + (options.jwt || auth.getJwt()))

  return fetch(`${API_URL}/api/manifests/${manifestId}/ship`, {
    method: 'POST',
    headers: headers,
    body: data
  })
    .then(async response => {
      const content = await response.json()
      if (!response.ok) {
        checkAuthError(response)
        throw new Error(content.data.error)
      }
      return content
    })
}

api.emailManifest = async (manifestId, lodgeCode, manifestNumber, file, options = {}) => {
  const data = new FormData()
  data.append('file', file, `manifest-${lodgeCode}-${manifestNumber}.pdf`)

  const headers = new Headers()
  headers.append('Authorization', 'Bearer ' + (options.jwt || auth.getJwt()))

  return fetch(`${API_URL}/api/manifests/${manifestId}/email-manifest`, {
    method: 'POST',
    headers: headers,
    body: data
  })
    .then(async response => {
      const content = await response.json()
      if (!response.ok) {
        checkAuthError(response)
        throw new Error(content.data.error)
      }
      return content
    })
}

api.closeManifest = async (manifestId, options = {}) => {
  const response = await fetch(`${API_URL}/api/manifests/${manifestId}/close`, defaultPostOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content
}

api.openManifest = async (manifestId, options = {}) => {
  const response = await fetch(`${API_URL}/api/manifests/${manifestId}/open`, defaultPostOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content
}

api.clearOrderManifest = async (orderId, options = {}) => {
  return fetch(`${API_URL}/api/orders/${orderId}/clear-manifest`, defaultPostOptions(options.jwt))
    .then(async response => {
      const content = await response.json()
      if (!response.ok) {
        checkAuthError(response)
        throw new Error(content.data.error)
      }
      return content.data
    })
}

api.assignOrderManifest = async (orderId, manifestId, options = {}) => {
  const body = {
    manifest_id: manifestId
  }
  return fetch(`${API_URL}/api/orders/${orderId}/assign-manifest`, defaultPostOptions(body, options.jwt))
    .then(async response => {
      const content = await response.json()
      if (!response.ok) {
        checkAuthError(response)
        throw new Error(content.data.error)
      }
      return content.data
    })
}

// api.receiveManifest = async (id, locationId) => {
//     return fetch(`${API_URL}/api/manifests/${id}/receive`, {
//         method: 'POST',
//         headers: {
//             'Accept': 'application/json',
//             'Content-Type': 'application/json'
//         },
//         body: JSON.stringify({
//             location_id: locationId
//         })
//     })
//     .then(async response => {
//         const content = await response.json()
//         if (!response.ok) {
//             throw new Error(content.data.error)
//         }
//         return content.data
//     })
// }

// api.weighInOrder = async (orderId, body) => {
//     return fetch(`${API_URL}/api/orders/${orderId}/weigh-in`, {
//         method: 'POST',
//         headers: {
//             'Accept': 'application/json',
//             'Content-Type': 'application/json'
//         },
//         body: JSON.stringify(body)
//     })
//     .then(async response => {
//         const content = await response.json()
//         if (!response.ok) {
//             throw new Error(content.data.error)
//         }
//         return content
//     })
// }

// api.updateWeighInOrder = async (orderId, body) => {
//     return fetch(`${API_URL}/api/orders/${orderId}/update-weigh-in`, {
//         method: 'POST',
//         headers: {
//             'Accept': 'application/json',
//             'Content-Type': 'application/json'
//         },
//         body: JSON.stringify(body)
//     })
//     .then(async response => {
//         const content = await response.json()
//         if (!response.ok) {
//             throw new Error(content.data.error)
//         }
//         return content
//     })
// }

// api.createSO = async (orderId, body) => {
//     return fetch(`${API_URL}/api/orders/${orderId}/create-so`, {
//         method: 'POST',
//         headers: {
//             'Accept': 'application/json',
//             'Content-Type': 'application/json'
//         },
//         body: JSON.stringify(body)
//     })
//     .then(async response => {
//         const content = await response.json()
//         if (!response.ok) {
//             throw new Error(content.data.error)
//         }
//         return content.data
//     })
// }

// api.syncFromMinotaur = async (orderId) => {
//     return fetch(`${API_URL}/api/orders/${orderId}/sync-from-minotaur`, {
//         method: 'POST',
//         headers: {
//             'Accept': 'application/json',
//             'Content-Type': 'application/json'
//         }
//     })
//     .then(async response => {
//         const content = await response.json()
//         if (!response.ok) {
//             throw new Error(content.data.error)
//         }
//         return content.data
//     })
// }

api.getSpecies = async (options = {}) => {
  const response = await fetch(`${API_URL}/api/species`, defaultOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

api.getProcesses = async (options = {}) => {
  const response = await fetch(`${API_URL}/api/processes`, defaultOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

api.getWeighInProcesses = async (options = {}) => {
  const response = await fetch(`${API_URL}/api/weighinprocesses`, defaultOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

api.getFeatureFlags = async (options = {}) => {
  const response = await fetch(`${API_URL}/api/featureflags`, defaultOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

api.deleteOrder = async (id, staffInitial, options = {}) => {
  const body = {
    staff_initial: staffInitial
  }

  const headers = new Headers({
    'Authorization': 'Bearer ' + (options.jwt || auth.getJwt()),
  })
  if (options.offlineHandle) {
    id = "$handle$" // special code
    headers.append("X-Upstream-Handle", options.offlineHandle)
  }

  const response = await fetch(`${API_URL}/api/orders/${id}`, {
    method: 'DELETE',
    headers: headers,
    body: JSON.stringify(body)
  })
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content
}

// api.getWeighInProcesses = async () => {
//     const response = await fetch(`${API_URL}/api/weighinprocesses`)
//     const content = await response.json()
//     if (!response.ok) {
//         throw new Error(content.data.error)
//     }
//     return content.data
// }

api.getLocations = async (options = {}) => {
  const response = await fetch(`${API_URL}/api/locations`, defaultOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

api.getExampleItems = async (options = {}) => {
  const response = await fetch(`${API_URL}/api/exampleitems`, defaultOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content
}

api.getExampleItem = async (id, options = {}) => {
  const response = await fetch(`${API_URL}/api/exampleitems/${id}`, defaultOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

api.createExampleItem = async (body, options = {}) => {
  return fetch(`${API_URL}/api/exampleitems`, defaultPostOptions(body, options.jwt))
    .then(async response => {
      const content = await response.json()
      if (!response.ok) {
        checkAuthError(response)
        throw new Error(content.data.error)
      }
      return content
    })
}

api.updateExampleItem = async (id, body, options = {}) => {
  const headers = new Headers()
  headers.append('Accept', 'application/json')
  headers.append('Content-Type', 'application/json')
  headers.append('Authorization', 'Bearer ' + (options.jwt || auth.getJwt()))

  const response = await fetch(`${API_URL}/api/exampleitems/${id}`, { method: 'PUT', headers: headers, body: JSON.stringify(body) })
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content
}

api.deleteExampleItem = async (id, options = {}) => {
  const response = await fetch(`${API_URL}/api/exampleitems/${id}`, { method: 'DELETE', headers: new Headers({ 'Authorization': 'Bearer ' + (options.jwt || auth.getJwt()), 'Accept': 'application/json' }) })
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content
}

api.getOptions = async (options = {}) => {
  const response = await fetch(`${API_URL}/api/options`, defaultOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

api.getCustomers = async (page = 1, perPage = 25, sortBy = "created_date", sortDirection = "desc", search = "", location = "", options = {}) => {
  const filters = options.filters ?? []
  const filtersParam = filters.length > 0 ? `&${filters.map(filter => `filters=${encodeURIComponent(filter)}`).join('&')}` : ''
  const response = await fetch(`${API_URL}/api/customers?page=${page}&per_page=${perPage}&sort_by=${sortBy}&sort_direction=${sortDirection}&search=${search}&location=${location}&updated_after=${options.updatedAfter ?? ""}${filtersParam}`, defaultOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content
}

api.createCustomer = async (body, options = {}) => {
  const response = await fetch(`${API_URL}/api/customers`, defaultPostOptions(body, options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content?.data?.error ?? "Failed to create customer")
  }
  return content
}

api.updateCustomer = async (customerId, body, options = {}) => {
  const headers = new Headers()
  headers.append('Accept', 'application/json')
  headers.append('Content-Type', 'application/json')
  headers.append('Authorization', 'Bearer ' + (options.jwt || auth.getJwt()))

  return fetch(`${API_URL}/api/customers/${customerId}`, {
    method: 'PUT',
    headers: headers,
    body: JSON.stringify(body)
  })
    .then(async response => {
      const content = await response.json()
      if (!response.ok) {
        checkAuthError(response)
        throw new Error(content.data.error)
      }
      return content
    })
}

api.importCustomers = async (csvFile, dryRun = false, duplicates = "SKIP", fishTagPadding = null, options = {}) => {
  var data = new FormData()
  data.append('file', csvFile)
  data.append('dry_run', String(dryRun))
  data.append('duplicates', duplicates) // Can be "SKIP", "CREATE", or "REPLACE"
  data.append('fish_tag_padding', fishTagPadding ?? '') // Use empty string instead of getting 'null'
  const response = await fetch(`${API_URL}/api/customers/import`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Authorization': 'Bearer ' + (options.jwt || auth.getJwt()),
    },
    body: data
  })
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    console.log("response")
    console.log(response)
    throw new Error(content.data.error)
  }
  return content.data
}

api.deleteCustomer = async (customerId, options = {}) => {
  return fetch(`${API_URL}/api/customers/${customerId}`, {
    method: 'DELETE',
    headers: new Headers({
      'Authorization': 'Bearer ' + (options.jwt || auth.getJwt()),
      'Accept': 'application/json'
    })
  })
    .then(async response => {
      const content = await response.json()
      if (!response.ok) {
        console.error(content.data.error)
        throw new Error(content.data.error)
      }
      return content
    })
}

// api.postBoxUpJpg = async (id, pdfBlob) => {
//     const data = new FormData()
//     data.append('file', pdfBlob)
//     const response = await fetch(`${API_URL}/api/orders/${id}/box-up-jpg`, {
//         method: 'POST',
//         body: data
//     })
//     const content = await response.json()
//     if (!response.ok) {
//         throw new Error(content.data.error)
//     }
//     return content.data
// }

api.uploadFile = async (orderId, file, filename = null, options = {}) => {
  const data = new FormData()
  if (filename) {
    data.append('file', file, filename)
  } else {
    data.append('file', file)
  }

  const headers = new Headers()
  headers.append('Authorization', 'Bearer ' + (options.jwt || auth.getJwt()))
  if (options.offlineHandle) {
    orderId = "$handle$" // special code
    headers.append("X-Upstream-Handle", options.offlineHandle)
  }

  const response = await fetch(`${API_URL}/api/orders/${orderId}/upload`, {
    method: 'POST',
    headers: headers,
    body: data
  })
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content
}

/** POST /api/customers/:id/address */
api.createCustomerAddress = async (customerId, body, options = {}) => {
  const response = await fetch(`${API_URL}/api/customers/${customerId}/address`, defaultPostOptions(body, options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content?.data?.error ?? "Failed to create customer address")
  }
  return content.data
}

/** PUT /api/customers/:id/address/:addressId */
api.updateCustomerAddress = async (customerId, addressId, body, options = {}) => {
  const headers = new Headers()
  headers.append('Accept', 'application/json')
  headers.append('Content-Type', 'application/json')
  headers.append('Authorization', 'Bearer ' + (options.jwt || auth.getJwt()))

  const response = await fetch(`${API_URL}/api/customers/${customerId}/address/${addressId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(body)
  })
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content?.data?.error ?? "Failed to update customer address")
  }
  return content.data
}

api.uploadLicense = async (orderId, file, options = {}) => {
  const data = new FormData()
  data.append('file', file)

  const headers = new Headers()
  headers.append('Authorization', 'Bearer ' + (options.jwt || auth.getJwt()))
  if (options.offlineHandle) {
    orderId = "$handle$" // special code
    headers.append("X-Upstream-Handle", options.offlineHandle)
  }

  const response = await fetch(`${API_URL}/api/orders/${orderId}/license`, {
    method: 'POST',
    headers: headers,
    body: data
  })
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content
}

api.uploadSignature = async (orderId, file, options = {}) => {
  const data = new FormData()
  data.append('file', file)

  const headers = new Headers()
  headers.append('Authorization', 'Bearer ' + (options.jwt || auth.getJwt()))
  if (options.offlineHandle) {
    orderId = "$handle$" // special code
    headers.append("X-Upstream-Handle", options.offlineHandle)
  }

  const response = await fetch(`${API_URL}/api/orders/${orderId}/signature`, {
    method: 'POST',
    headers: headers,
    body: data
  })
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content
}

api.deleteFile = async (orderId, filename, options = {}) => {
  const body = { filename: filename }

  const headers = new Headers()
  headers.append('Authorization', 'Bearer ' + (options.jwt || auth.getJwt()))
  headers.append('Accept', 'application/json')
  headers.append('Content-Type', 'application/json')
  if (options.offlineHandle) {
    orderId = "$handle$" // special code
    headers.append("X-Upstream-Handle", options.offlineHandle)
  }

  const response = await fetch(`${API_URL}/api/orders/${orderId}/delete-file`, {
    method: 'POST',
    headers: headers,
    body: body ? JSON.stringify(body) : undefined
  })
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content
}

/**
 * @type {(orderId: number, licenseFile: File, options?: { jwt?: string }) => Promise<string>}
 * @returns The relative path of the uploaded file on the server (e.g., 2342594354/license-20250101121212.jpg)
 */
api.uploadOrderLicenseFront = async (orderId, licenseFile, options = {}) => {
  const data = new FormData()
  data.append('file', licenseFile)
  const headers = new Headers()
  headers.append('Authorization', 'Bearer ' + (options.jwt || auth.getJwt()))
  if (options.offlineHandle) {
    orderId = "$handle$" // special code
    headers.append("X-Upstream-Handle", options.offlineHandle)
  }
  const response = await fetch(`${API_URL}/api/orders/${orderId}/license-front`, {
    method: 'POST',
    headers: headers,
    body: data
  })
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

/**
 * @type {(orderId: number, licenseFile: File, options?: { jwt?: string }) => Promise<string>}
 * @returns The relative path of the uploaded file on the server (e.g., 2342594354/license-20250101121212.jpg)
 */
api.uploadOrderLicenseBack = async (orderId, licenseFile, options = {}) => {
  const data = new FormData()
  data.append('file', licenseFile)
  const headers = new Headers()
  headers.append('Authorization', 'Bearer ' + (options.jwt || auth.getJwt()))
  if (options.offlineHandle) {
    orderId = "$handle$" // special code
    headers.append("X-Upstream-Handle", options.offlineHandle)
  }
  const response = await fetch(`${API_URL}/api/orders/${orderId}/license-back`, {
    method: 'POST',
    headers: headers,
    body: data
  })
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

/** @type {(orderId: number, blob: Blob, options?: { jwt?: string }) => Promise<any>} */
api.postSignature = async (orderId, blob, options = {}) => {
  const signatureData = new FormData()
  signatureData.append('file', new File([blob], "signature.png"))
  const headers = new Headers()
  headers.append('Authorization', 'Bearer ' + (options.jwt || auth.getJwt()))
  if (options.offlineHandle) {
    orderId = "$handle$" // special code
    headers.append("X-Upstream-Handle", options.offlineHandle)
  }
  const response = await fetch(`${API_URL}/api/orders/${orderId}/signature`, {
    method: 'POST',
    headers: headers,
    body: signatureData
  })
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

// Order Templates
api.getOrderTemplates = async (options = {}) => {
  const response = await fetch(`${API_URL}/api/order-templates`, defaultOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

api.createOrderTemplate = async (body, options = {}) => {
  const response = await fetch(`${API_URL}/api/order-templates`, defaultPostOptions(body, options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

api.deleteOrderTemplate = async (id, options = {}) => {
  const response = await fetch(`${API_URL}/api/order-templates/${id}`, {
    method: 'DELETE',
    headers: new Headers({
      'Authorization': 'Bearer ' + (options.jwt || auth.getJwt()),
      'Accept': 'application/json'
    })
  })
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

/** @type {(orderId: number, totalBoxes: number, options?: { jwt?: string }) => Promise<Order>} */
api.setOrderBoxes = async (orderId, totalBoxes, options = {}) => {
  const headers = new Headers()
  headers.append('Accept', 'application/json')
  headers.append('Content-Type', 'application/json')
  headers.append('Authorization', 'Bearer ' + (options.jwt || auth.getJwt()))

  return fetch(`${API_URL}/api/orders/${orderId}/boxes`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ total_boxes: totalBoxes })
  })
    .then(async response => {
      const content = await response.json()
      if (!response.ok) {
        checkAuthError(response)
        throw new Error(content.data.error)
      }
      return content.data
    })
}

/** @type {(year?: number, options?: { jwt?: string }) => Promise<{ data: any[], total: number }>} */
api.getTripCodes = async (year, options = {}) => {
  const yearParam = year != null && Number.isFinite(year) ? `?year=${year}` : ""
  const response = await fetch(`${API_URL}/api/tripcodes${yearParam}`, defaultOptions(options.jwt))
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content?.data?.error ?? "Failed to fetch trip codes")
  }
  return content
}

/**
 * Fetch all customers for a given trip code (paginates through all matches).
 *
 * @type {(tripCode: string, year?: number) => Promise<any[]>}
 */
api.getCustomersByTripCode = async (tripCode, year) => {
  const code = String(tripCode ?? "").trim()
  if (!code) {
    return []
  }
  const filters = [`{trip_code = ${code}}`]
  const perPage = 100
  const all = []
  let page = 1

  while (true) {
    const body = await api.getCustomers(page, perPage, "created_date", "desc", "", "", { filters })
    const batch = body?.data ?? []
    all.push(...batch)
    const total = body?.total ?? batch.length
    if (all.length >= total || batch.length < perPage) {
      break
    }
    page += 1
  }

  return all
}

/** @type {(id: string, options?: { jwt?: string }) => Promise<any>} */
api.getTripCode = async (id, options = {}) => {
  const response = await fetch(
    `${API_URL}/api/tripcodes/${encodeURIComponent(id)}`,
    defaultOptions(options.jwt)
  )
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content?.data?.error ?? "Failed to fetch trip code")
  }
  return content.data
}

/** @type {(orderId: number) => Promise<any>} */
api.cloneOrderV2 = async (orderId) => {
  const response = await fetch(`${API_URL}/api/orders/${orderId}/clone-v2`, defaultPostOptions())
  const content = await response.json()
  if (!response.ok) {
    checkAuthError(response)
    throw new Error(content.data.error)
  }
  return content.data
}

/** @returns {Promise<{ status: number, body: unknown }>} */
api.invalidateDataTableCaches = async () => {
  const response = await fetch(`${API_URL}/api/utilities/invalidate-datatable-caches`, defaultOptions())
  const text = await response.text()
  let body
  try {
    body = text ? JSON.parse(text) : null
  } catch {
    body = text
  }
  checkAuthError(response)
  return { status: response.status, body }
}

export default api